import { useState, useEffect, Fragment } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import api from '../services/api'
import {
  SESSION_NUMBER,
  FORM_TITLE,
  FORM_SUBTITLE,
  SECTIONS,
  OTHERS_OPTION,
  normalizeSession12FormData,
  getSectionOptOutLabel,
  stripAllSession12OptOuts,
} from '../config/session12DailyRitualsForm'

const SectionCheckboxGroup = ({
  sectionId,
  habitOptions,
  optOutLabel,
  value = [],
  othersText = '',
  onToggle,
  onOthersTextChange,
  error,
}) => {
  const allOptions = [...habitOptions, optOutLabel, OTHERS_OPTION]

  return (
    <div className="space-y-2">
      {allOptions.map((option) => {
        const checked = value.includes(option)
        return (
          <div key={option}>
            <div
              onClick={() => onToggle(sectionId, option)}
              className="flex items-start gap-3 cursor-pointer group"
            >
              <div
                className={`w-5 h-5 mt-0.5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                  checked ? 'border-purple-600 bg-purple-600' : 'border-gray-300 group-hover:border-purple-400'
                }`}
              >
                {checked && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="text-gray-700 text-sm leading-snug">{option}</span>
            </div>
            {option === OTHERS_OPTION && checked && (
              <input
                type="text"
                value={othersText}
                onChange={(e) => onOthersTextChange?.(e.target.value)}
                placeholder="Please specify"
                className="mt-2 ml-8 w-[calc(100%-2rem)] border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </div>
        )
      })}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  )
}

const Session12DailyRitualsFormModal = ({
  isOpen,
  onClose,
  programSlug,
  existingResponse,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({})
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (isOpen) {
      if (existingResponse?.formData) {
        setFormData(normalizeSession12FormData(existingResponse.formData))
      } else {
        setFormData({})
      }
      setErrors({})
    }
  }, [isOpen, existingResponse])

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }))
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
    }
  }

  const handleToggle = (sectionId, option) => {
    const key = sectionId
    const othersKey = `${sectionId}_othersText`
    const section = SECTIONS.find((s) => s.id === sectionId)
    const optOutLabel = getSectionOptOutLabel(section)
    const isOptOut = option === optOutLabel

    setFormData((prev) => {
      const current = prev[key] || []
      let next
      const patch = {}

      if (current.includes(option)) {
        next = current.filter((v) => v !== option)
      } else if (isOptOut) {
        next = [optOutLabel]
        patch[othersKey] = ''
      } else {
        next = [...stripAllSession12OptOuts(current), option]
      }

      return { ...prev, [key]: next, ...patch }
    })

    setErrors((prev) => {
      if (!prev[key] && !prev[othersKey]) return prev
      const next = { ...prev }
      delete next[key]
      delete next[othersKey]
      return next
    })
  }

  const handleOthersTextChange = (sectionId, text) => {
    updateField(`${sectionId}_othersText`, text)
  }

  const validate = () => {
    const newErrors = {}
    for (const section of SECTIONS) {
      const key = section.id
      const val = formData[key]
      const othersText = formData[`${key}_othersText`] || ''
      if (!val || val.length === 0) {
        newErrors[key] = 'Select at least one option'
      } else if (val.includes(OTHERS_OPTION) && !othersText.trim()) {
        newErrors[key] = 'Please add details for Others'
      }
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    setIsSubmitting(true)
    try {
      await api.post(`/api/programs/${programSlug}/session-forms`, {
        sessionNumber: SESSION_NUMBER,
        formData,
      })
      onSuccess?.()
      onClose()
    } catch (err) {
      setErrors({ _general: err.response?.data?.message || 'Failed to submit form' })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-start justify-center pt-4 sm:pt-10 px-3 sm:px-4 pb-4 overflow-y-auto"
      >
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.97 }}
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg my-4"
        >
          <div className="sticky top-0 bg-white rounded-t-2xl border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
            <div>
              <h2 className="text-lg font-bold text-gray-900">{FORM_TITLE}</h2>
              <p className="text-xs text-gray-500">Session 12 · {FORM_SUBTITLE}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-5 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
            {errors._general && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                {errors._general}
              </div>
            )}

            {SECTIONS.map((section) => (
              <Fragment key={section.id}>
                {section.groupTitle && (
                  <div className="pt-2 pb-1 -mt-1">
                    <h3 className="text-base font-bold text-gray-900 border-b border-purple-200 pb-2">
                      {section.groupTitle}
                    </h3>
                  </div>
                )}
                <div className="bg-gray-50 rounded-xl p-4">
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    {section.title}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <SectionCheckboxGroup
                    sectionId={section.id}
                    habitOptions={section.options}
                    optOutLabel={getSectionOptOutLabel(section)}
                    value={formData[section.id] || []}
                    othersText={formData[`${section.id}_othersText`] || ''}
                    onToggle={handleToggle}
                    onOthersTextChange={(text) => handleOthersTextChange(section.id, text)}
                    error={errors[section.id]}
                  />
                </div>
              </Fragment>
            ))}
          </div>

          <div className="sticky bottom-0 bg-white rounded-b-2xl border-t border-gray-100 px-5 py-4">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Submitting...' : existingResponse ? 'Update response' : 'Submit'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default Session12DailyRitualsFormModal
